import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ShieldCheckIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import AttackPathsTab from './AttackPathsTab';
import { SafeHtmlContent } from '../components/SafeHtmlContent';

// ── Effort classification ─────────────────────────────────────────────────────

export const HIGH_EFFORT_KEYWORDS = [
  'private link', 'private endpoint', 'virtual network', 'vnet',
  'migrate', 'rotate', 'recreate', 'deploy agent', 'install agent',
  'encryption at rest',
];

export const LOW_EFFORT_KEYWORDS = [
  'enable', 'toggle', 'turn on', 'set to on', 'notification',
  'email', 'contact', 'diagnostic logs', 'select save', 'auditing',
];

export const EFFORT_LABELS = {
  2: '~15–30 min',
  3: '~Half day',
  4: 'Multi-day project',
};

export function estimateEffort(recName, remediationText) {
  const name = recName.toLowerCase();
  const rem = (remediationText ?? '').toLowerCase();
  for (const kw of HIGH_EFFORT_KEYWORDS) {
    if (name.includes(kw) || rem.includes(kw)) return 4;
  }
  for (const kw of LOW_EFFORT_KEYWORDS) {
    if (name.includes(kw) || rem.includes(kw)) return 2;
  }
  return 3;
}

// ── Cost classification ───────────────────────────────────────────────────────

export const BUDGET_REQUIRED_KEYWORDS = [
  'microsoft defender for resource manager',
  'microsoft defender for storage',
  'microsoft defender cspm',
  'microsoft defender for app service',
  'microsoft defender for azure sql',
  'microsoft defender for key vault',
  'microsoft defender for apis',
  'microsoft defender for containers',
  'microsoft defender for sql',
  'microsoft defender for servers',
  'microsoft defender for open-source',
  'microsoft defender for dns',
  'azure backup should be enabled',
];

export const INFRA_COST_KEYWORDS = [
  'private link',
  'private endpoint',
  'virtual network rules',
  'azure firewall',
];

export const MINOR_COST_KEYWORDS = [
  'diagnostic logs',
  'auditing',
  'log analytics',
];

export const COST_LABELS = {
  budget: 'Budget required',
  infra:  'Infrastructure cost',
  minor:  'Minor cost',
  free:   'No cost',
};

export const COST_DETAIL = {
  budget: 'Enabling a paid Microsoft Defender plan. Requires budget approval before actioning — do not enable without sign-off.',
  infra:  'Deploying Azure resources such as private endpoints or VNet rules. Carries an ongoing infrastructure cost — estimate spend before proceeding.',
  minor:  'Enabling diagnostic logging or auditing. Minimal storage/Log Analytics cost — usually covered by existing spend, but confirm with your workspace owner.',
  free:   'Pure configuration change. No additional licensing or Azure resource cost required.',
};

const COST_ICONS = { budget: '🔴', infra: '🟠', minor: '🟡', free: '🟢' };

export function classifyCost(recName) {
  const name = recName.toLowerCase();
  for (const kw of BUDGET_REQUIRED_KEYWORDS) {
    if (name.includes(kw)) return 'budget';
  }
  for (const kw of INFRA_COST_KEYWORDS) {
    if (name.includes(kw)) return 'infra';
  }
  for (const kw of MINOR_COST_KEYWORDS) {
    if (name.includes(kw)) return 'minor';
  }
  return 'free';
}

// ── Style maps ────────────────────────────────────────────────────────────────

const SEV_COLORS = {
  High:   'bg-red-100 text-red-800',
  Medium: 'bg-amber-100 text-amber-800',
  Low:    'bg-blue-100 text-blue-800',
};

const EFFORT_COLORS = {
  2: 'bg-green-100 text-green-800',
  3: 'bg-amber-100 text-amber-800',
  4: 'bg-red-100 text-red-800',
};

const COST_BADGE_STYLES = {
  budget: 'bg-red-50 text-red-800 border-red-200',
  infra:  'bg-orange-50 text-orange-800 border-orange-200',
  minor:  'bg-yellow-50 text-yellow-700 border-yellow-200',
  free:   'bg-green-50 text-green-800 border-green-200',
};

const COST_CALLOUT_BG = {
  budget: 'bg-red-50 border-red-300',
  infra:  'bg-orange-50 border-orange-200',
  minor:  'bg-yellow-50 border-yellow-200',
  free:   'bg-green-50 border-green-200',
};

const COST_CALLOUT_TEXT = {
  budget: 'text-red-900',
  infra:  'text-orange-900',
  minor:  'text-yellow-900',
  free:   'text-green-900',
};

const TIER_CONFIG = {
  1: { label: 'P1 — Act Now',           desc: 'High severity · Non-negotiable',               headerBg: 'bg-red-50',   accent: '#EF4444', countBg: 'bg-red-600 text-white'   },
  2: { label: 'P2 — Plan This',         desc: 'Medium severity · Schedule for next sprint',   headerBg: 'bg-amber-50', accent: '#F59E0B', countBg: 'bg-amber-500 text-white' },
  3: { label: 'P3 — Scheduled Hygiene', desc: 'Low severity · Include in routine maintenance', headerBg: 'bg-blue-50',  accent: '#3B82F6', countBg: 'bg-blue-500 text-white'  },
};

const TIER_LABELS = { 1: 'P1 — Act Now', 2: 'P2 — Plan This', 3: 'P3 — Scheduled Hygiene' };

// ── Data helpers ──────────────────────────────────────────────────────────────

function deriveRecommendations(visibleSubscriptions, subData) {
  const map = new Map();

  for (const sub of visibleSubscriptions) {
    const d = subData[sub.subscriptionId];
    if (!d || d.loading || d.error) continue;
    for (const rec of d.assessments ?? []) {
      const p = rec.properties ?? {};
      const m = p.metadata ?? {};
      const name = p.displayName ?? rec.name ?? 'Unknown';

      const severity    = m.severity ?? 'Low';
      const category    = Array.isArray(m.categories) && m.categories.length > 0 ? m.categories[0] : 'General';
      const description = m.description ?? '';
      const remediation = m.remediationDescription ?? '';
      const resources   = p.resources ?? [];

      if (!map.has(name)) {
        const effort        = estimateEffort(name, remediation);
        const costTier      = classifyCost(name);
        const tier          = severity === 'High' ? 1 : severity === 'Medium' ? 2 : 3;
        const assessmentKey = rec.name;
        const azurePortalUri = assessmentKey
          ? `https://portal.azure.com/#blade/Microsoft_Azure_Security/RecommendationsBlade/assessmentKey/${assessmentKey}`
          : null;
        map.set(name, { displayName: name, assessmentKey, severity, category, numSubscriptions: 1, totalResources: resources.length, description, remediationDescription: remediation, azurePortalUri, tier, effort, costTier });
      } else {
        const existing = map.get(name);
        existing.numSubscriptions++;
        existing.totalResources += resources.length;
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.numSubscriptions !== b.numSubscriptions) return b.numSubscriptions - a.numSubscriptions;
    return a.effort - b.effort;
  });
}

function filterRecommendations(recs, { category, effort, cost }) {
  return recs.filter(r => {
    if (category !== 'All' && r.category !== category) return false;
    if (effort  !== 'All' && r.effort   !== Number(effort)) return false;
    if (cost    !== 'All' && r.costTier !== cost) return false;
    return true;
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

const SeverityBadge = ({ severity }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEV_COLORS[severity] ?? SEV_COLORS.Low}`}>
    {severity ?? '—'}
  </span>
);

const EffortTag = ({ effort }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${EFFORT_COLORS[effort] ?? EFFORT_COLORS[3]}`}>
    {EFFORT_LABELS[effort] ?? '—'}
  </span>
);

const CostBadge = ({ costTier }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${COST_BADGE_STYLES[costTier] ?? COST_BADGE_STYLES.free}`}>
    {COST_ICONS[costTier]} {COST_LABELS[costTier]}
  </span>
);

const StatCard = ({ label, value, color, sub }) => (
  <div className="bg-white shadow rounded-lg p-5">
    <p className="text-sm text-gray-500">{label}</p>
    <p className={`text-3xl font-bold mt-1 ${color ?? 'text-gray-900'}`}>{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const RecommendationSummary = ({ recommendations }) => {
  const p1     = recommendations.filter(r => r.tier === 1).length;
  const p2     = recommendations.filter(r => r.tier === 2).length;
  const p3     = recommendations.filter(r => r.tier === 3).length;
  const noCost = recommendations.filter(r => r.costTier === 'free').length;
  const budget = recommendations.filter(r => r.costTier === 'budget').length;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
      <StatCard label="P1 — Act Now"    value={p1}     color={p1 > 0     ? 'text-red-600'   : 'text-gray-900'} sub="High severity"   />
      <StatCard label="P2 — Plan This"  value={p2}     color={p2 > 0     ? 'text-amber-600' : 'text-gray-900'} sub="Medium severity" />
      <StatCard label="P3 — Hygiene"    value={p3}     color="text-blue-600"                                   sub="Low severity"    />
      <StatCard label="No Cost"         value={noCost} color="text-green-600"                                  sub="Act immediately" />
      <StatCard label="Budget Required" value={budget} color={budget > 0 ? 'text-red-600'   : 'text-gray-900'} sub="Needs approval"  />
    </div>
  );
};

const CostSummaryBanner = ({ recommendations }) => {
  const counts = { budget: 0, infra: 0, minor: 0, free: 0 };
  for (const r of recommendations) counts[r.costTier] = (counts[r.costTier] ?? 0) + 1;
  const total       = recommendations.length;
  const noBudgetPct = total > 0 ? Math.round(((total - counts.budget) / total) * 100) : 0;
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-base font-semibold text-blue-900">
            {noBudgetPct}% of recommendations require no additional budget
          </p>
          {counts.budget > 0 && (
            <p className="text-sm text-red-700 mt-1 font-medium">
              ⚠ {counts.budget} recommendation{counts.budget !== 1 ? 's' : ''} require budget approval — do not action without sign-off.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {(['budget', 'infra', 'minor', 'free']).map(tier => (
            <div key={tier} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-md px-3 py-1.5">
              <span>{COST_ICONS[tier]}</span>
              <span className="text-sm font-semibold text-gray-900">{counts[tier]}</span>
              <span className="text-xs text-gray-500">{COST_LABELS[tier]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RecommendationFilters = ({ recommendations, filters, onChange, visibleCount }) => {
  const categories = ['All', ...[...new Set(recommendations.map(r => r.category))].sort()];
  return (
    <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2">
        <FunnelIcon className="h-4 w-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Filter</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Category</label>
        <select value={filters.category} onChange={e => onChange({ ...filters, category: e.target.value })}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Effort</label>
        <select value={filters.effort} onChange={e => onChange({ ...filters, effort: e.target.value })}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="All">All</option>
          <option value="2">Easy (~15–30 min)</option>
          <option value="3">Medium (~Half day)</option>
          <option value="4">Complex (Multi-day)</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Cost Impact</label>
        <select value={filters.cost} onChange={e => onChange({ ...filters, cost: e.target.value })}
          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="All">All</option>
          <option value="free">No cost</option>
          <option value="minor">Minor cost</option>
          <option value="infra">Infrastructure cost</option>
          <option value="budget">Budget required</option>
        </select>
      </div>
      <span className="ml-auto text-sm text-gray-500">{visibleCount} of {recommendations.length} shown</span>
    </div>
  );
};

const PriorityTierSection = ({ tier, items, collapsedByDefault, onSelectRec }) => {
  const [collapsed, setCollapsed] = useState(collapsedByDefault);
  const cfg = TIER_CONFIG[tier];
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mb-4" style={{ borderLeft: `4px solid ${cfg.accent}` }}>
      <button
        onClick={() => setCollapsed(c => !c)}
        className={`w-full flex items-center gap-3 px-5 py-4 text-left ${cfg.headerBg} transition-colors`}
      >
        <span className={`min-w-[1.75rem] h-7 flex items-center justify-center rounded px-1.5 text-xs font-bold ${cfg.countBg}`}>
          {items.length}
        </span>
        <div className="flex-1 text-left">
          <p className="font-semibold text-gray-900">{cfg.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{cfg.desc}</p>
        </div>
        {collapsed
          ? <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
          : <ChevronDownIcon  className="h-4 w-4 text-gray-400 flex-shrink-0" />}
      </button>
      {!collapsed && (
        items.length === 0 ? (
          <p className="px-5 py-4 text-sm text-gray-400">No recommendations in this tier.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Recommendation</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-24">Subs</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Effort</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost Impact</th>
                  <th className="px-4 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((rec, i) => (
                  <tr key={rec.displayName} onClick={() => onSelectRec(rec)} className="hover:bg-gray-50 cursor-pointer group">
                    <td className="px-4 py-3 text-xs text-gray-400 font-mono">{i + 1}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 leading-snug">{rec.displayName}</p>
                      <p className="text-xs text-gray-400 mt-0.5">{rec.category}</p>
                    </td>
                    <td className="px-4 py-3"><SeverityBadge severity={rec.severity} /></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-semibold text-gray-700">{rec.numSubscriptions}</span>
                      <span className="text-xs text-gray-400 ml-1">subs</span>
                    </td>
                    <td className="px-4 py-3"><EffortTag effort={rec.effort} /></td>
                    <td className="px-4 py-3"><CostBadge costTier={rec.costTier} /></td>
                    <td className="px-4 py-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      {rec.azurePortalUri && (
                        <a href={rec.azurePortalUri} target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()} title="Open in Azure Portal"
                          className="text-gray-400 hover:text-blue-600 transition-colors">
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export const RecommendationDetailPanel = ({ rec, onClose }) => {
  if (!rec) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-xl flex flex-col border-l border-gray-200">
      <div className="flex items-start justify-between px-6 py-4 border-b bg-gray-50 flex-shrink-0">
        <div className="flex-1 min-w-0 pr-4">
          <p className="text-xs font-medium text-gray-500">{TIER_LABELS[rec.tier]}</p>
          <h2 className="text-base font-semibold text-gray-900 mt-1 leading-snug">{rec.displayName}</h2>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-0.5">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Severity</p>
            <div className="mt-1.5"><SeverityBadge severity={rec.severity} /></div>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Priority Tier</p>
            <p className="text-sm text-gray-800 mt-1.5 font-medium">{TIER_LABELS[rec.tier]}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Subscriptions Affected</p>
            <p className="text-sm text-gray-800 mt-1.5">{rec.numSubscriptions} subscription{rec.numSubscriptions !== 1 ? 's' : ''}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Effort</p>
            <div className="mt-1.5"><EffortTag effort={rec.effort} /></div>
          </div>
        </div>
        <div className={`border rounded-lg p-4 ${COST_CALLOUT_BG[rec.costTier]}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl leading-none">{COST_ICONS[rec.costTier]}</span>
            <p className={`font-semibold text-sm ${COST_CALLOUT_TEXT[rec.costTier]}`}>{COST_LABELS[rec.costTier]}</p>
          </div>
          <p className={`text-sm leading-relaxed ${COST_CALLOUT_TEXT[rec.costTier]}`}>{COST_DETAIL[rec.costTier]}</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Why this matters</p>
          <SafeHtmlContent className="text-sm text-gray-700 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_b]:font-semibold"
            html={rec.description} />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">How to fix it</p>
          <SafeHtmlContent className="text-sm text-gray-700 leading-relaxed [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_b]:font-semibold"
            html={rec.remediationDescription} />
        </div>
        {rec.azurePortalUri && (
          <div className="pt-2 border-t border-gray-200">
            <a href={rec.azurePortalUri} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Open in Azure Portal →
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const EffortMatrixView = ({ recommendations, onSelectRec }) => {
  const QUADRANTS = [
    { key: 'high-easy',    label: 'High Impact / Easy',    desc: 'Quick wins — do first',        color: 'bg-green-50 border-green-200' },
    { key: 'high-complex', label: 'High Impact / Complex', desc: 'Plan & resource carefully',     color: 'bg-amber-50 border-amber-200' },
    { key: 'low-easy',     label: 'Low Impact / Easy',     desc: 'Fill in gaps when time allows', color: 'bg-blue-50 border-blue-200'   },
    { key: 'low-complex',  label: 'Low Impact / Complex',  desc: 'Consider deferring',            color: 'bg-gray-50 border-gray-200'   },
  ];
  const qKey = rec => `${rec.tier <= 2 ? 'high' : 'low'}-${rec.effort <= 2 ? 'easy' : 'complex'}`;
  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-sm text-amber-900">
        <strong>Planning tool only.</strong> Use this to plan implementation sprints, not to decide priority.
        The P1/P2/P3 tiers are the definitive action order.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {QUADRANTS.map(q => {
          const items = recommendations.filter(r => qKey(r) === q.key);
          return (
            <div key={q.key} className={`border rounded-lg p-4 ${q.color}`}>
              <div className="mb-3">
                <p className="font-semibold text-gray-900 text-sm">{q.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {q.desc} · <span className="font-medium">{items.length}</span> item{items.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {items.length === 0 ? (
                  <p className="text-xs text-gray-400">No items</p>
                ) : items.map(rec => (
                  <button key={rec.displayName} onClick={() => onSelectRec(rec)}
                    title={`${COST_ICONS[rec.costTier]} ${COST_LABELS[rec.costTier]}`}
                    className="group inline-flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-1 text-xs text-gray-800 hover:bg-gray-50 hover:border-gray-300 transition-colors max-w-[220px]">
                    <span className="truncate">{rec.displayName}</span>
                    <span className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">{COST_ICONS[rec.costTier]}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Exported tab component ────────────────────────────────────────────────────

export default function RecommendationsTab({ visibleSubscriptions, subData, isAllLoaded }) {
  const [filters, setFilters]         = useState({ category: 'All', effort: 'All', cost: 'All' });
  const [selectedRec, setSelectedRec] = useState(null);
  const [activeTab, setActiveTab]     = useState('priority');

  const allRecommendations = isAllLoaded ? deriveRecommendations(visibleSubscriptions, subData) : [];
  const filteredRecs       = filterRecommendations(allRecommendations, filters);
  const p1Items = filteredRecs.filter(r => r.tier === 1);
  const p2Items = filteredRecs.filter(r => r.tier === 2);
  const p3Items = filteredRecs.filter(r => r.tier === 3);

  const totalAttackPaths = isAllLoaded
    ? visibleSubscriptions.reduce((n, sub) => n + (subData[sub.subscriptionId]?.attackPaths?.length ?? 0), 0)
    : 0;

  if (!isAllLoaded) return null;

  return (
    <>
      {visibleSubscriptions.length === 0 && (
        <p className="text-center text-gray-400 py-16">No subscriptions match this view.</p>
      )}

      {visibleSubscriptions.length > 0 && (
        <>
          {/* Summary + cost banner — only on rec tabs when there are recommendations */}
          {activeTab !== 'attackpaths' && allRecommendations.length > 0 && (
            <>
              <RecommendationSummary recommendations={allRecommendations} />
              <CostSummaryBanner recommendations={filteredRecs} />
            </>
          )}

          {/* Tab bar */}
          <div className="flex gap-0.5 border-b border-gray-200 mb-6">
            {[
              { id: 'priority',    label: 'Priority View', Icon: ListBulletIcon, note: 'Default',       count: null               },
              { id: 'matrix',      label: 'Effort Matrix',  Icon: Squares2X2Icon, note: 'Planning tool', count: null               },
              { id: 'attackpaths', label: 'Attack Paths',   Icon: BoltIcon,       note: null,            count: totalAttackPaths   },
            ].map(({ id, label, Icon, note, count }) => (
              <button key={id} onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>
                <Icon className="h-4 w-4" />
                {label}
                {count != null && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${count > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                    {count}
                  </span>
                )}
                {note && <span className="text-xs opacity-50 hidden sm:inline">· {note}</span>}
              </button>
            ))}
          </div>

          {/* Rec tabs: empty state */}
          {activeTab !== 'attackpaths' && allRecommendations.length === 0 && (
            <div className="text-center py-16">
              <ShieldCheckIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
              <p className="text-gray-700 font-medium">No open recommendations</p>
              <p className="text-sm text-gray-400 mt-1">No unhealthy assessments found for the selected subscriptions.</p>
            </div>
          )}

          {/* Rec tabs: filters */}
          {activeTab !== 'attackpaths' && allRecommendations.length > 0 && (
            <RecommendationFilters
              recommendations={allRecommendations}
              filters={filters}
              onChange={setFilters}
              visibleCount={filteredRecs.length}
            />
          )}

          {/* Rec tabs: no filter results */}
          {activeTab !== 'attackpaths' && allRecommendations.length > 0 && filteredRecs.length === 0 && (
            <div className="text-center py-12 bg-white shadow rounded-lg">
              <p className="text-gray-500 font-medium">No recommendations match the current filters</p>
              <button onClick={() => setFilters({ category: 'All', effort: 'All', cost: 'All' })}
                className="mt-3 text-sm text-blue-600 hover:underline">
                Clear filters
              </button>
            </div>
          )}

          {/* Priority view */}
          {activeTab === 'priority' && filteredRecs.length > 0 && (
            <>
              <PriorityTierSection tier={1} items={p1Items} collapsedByDefault={false} onSelectRec={setSelectedRec} />
              <PriorityTierSection tier={2} items={p2Items} collapsedByDefault={false} onSelectRec={setSelectedRec} />
              <PriorityTierSection tier={3} items={p3Items} collapsedByDefault={true}  onSelectRec={setSelectedRec} />
            </>
          )}

          {/* Effort matrix */}
          {activeTab === 'matrix' && filteredRecs.length > 0 && (
            <EffortMatrixView recommendations={filteredRecs} onSelectRec={setSelectedRec} />
          )}

          {/* Attack paths */}
          {activeTab === 'attackpaths' && (
            <AttackPathsTab
              visibleSubscriptions={visibleSubscriptions}
              subData={subData}
              allRecommendations={allRecommendations}
              onSelectRec={setSelectedRec}
            />
          )}
        </>
      )}

      {selectedRec && (
        <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-20" onClick={() => setSelectedRec(null)} />
          <RecommendationDetailPanel rec={selectedRec} onClose={() => setSelectedRec(null)} />
        </>
      )}
    </>
  );
}
